import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TemplatePage from "../TemplatePage";
import { bsv } from "scrypt-ts"; // or import bsv from 'bsv'
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Alert,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Divider,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const CreateWallet: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [address, setAddress] = useState<string>("");
  const [privateKeyWif, setPrivateKeyWif] = useState<string>("");
  const [showPrivateKey, setShowPrivateKey] = useState<boolean>(false);
  const [savedKeyChecked, setSavedKeyChecked] = useState<boolean>(false);

  const generateWallet = () => {
    // Generate private key
    const pk = bsv.PrivateKey.fromRandom();
    const addr = pk.toAddress().toString();
    const wif = pk.toWIF();

    setPrivateKeyWif(wif);
    setAddress(addr);
    setSavedKeyChecked(false);
    setShowPrivateKey(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      alert(t('createWallet.copied'));
    });
  };

  return (
    <TemplatePage>
      <Container maxWidth="sm" sx={{ mt: 5 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            {t('createWallet.title')}
          </Typography>

          <Typography variant="body1" color="text.secondary" paragraph align="center">
            {t('createWallet.desc')}
          </Typography>

          {!privateKeyWif ? (
            <Box display="flex" justifyContent="center" mt={3}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={generateWallet}
              >
                {t('createWallet.generateKeys')}
              </Button>
            </Box>
          ) : (
            <Box mt={4}>
              <Alert severity="success" sx={{ mb: 3 }}>
                {t('createWallet.success')}
              </Alert>

              <Typography variant="subtitle1" fontWeight="bold">
                {t('createWallet.publicAddress')}
              </Typography>
              <Box display="flex" mb={3}>
                <TextField
                  fullWidth
                  value={address}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => copyToClipboard(address)} edge="end">
                          <ContentCopyIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Alert severity="error" sx={{ mb: 2 }}>
                {t('createWallet.warning')}
              </Alert>

              <Typography variant="subtitle1" fontWeight="bold">
                {t('createWallet.privateKey')}
              </Typography>
              <Box display="flex" mb={3}>
                <TextField
                  fullWidth
                  type={showPrivateKey ? "text" : "password"}
                  value={privateKeyWif}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPrivateKey(!showPrivateKey)} edge="end">
                          {showPrivateKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                        <IconButton onClick={() => copyToClipboard(privateKeyWif)} edge="end">
                          <ContentCopyIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={savedKeyChecked}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSavedKeyChecked(e.target.checked)}
                    color="primary"
                  />
                }
                label={t('createWallet.confirmCopied')}
              />

              <Box display="flex" justifyContent="center" mt={3}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={!savedKeyChecked}
                  onClick={() => navigate("/")}
                >
                  {t('createWallet.accessWallet')}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>
    </TemplatePage>
  );
};

export default CreateWallet;
